import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Box, Flex, Grid, Select, Spinner, Text } from "@chakra-ui/react";
import useIdentity from "@/features/ui/hooks/use-identity";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import AppListHeader from "@/features/ui/app-list-header";
import AppQueryController from "@/features/ui/app-query-controller";
import useGetBoutiqueAllOrderedProducts from "@/features/line-sheet-sets/hooks/use-get-boutique-all-ordered-products";
import AllOrderedProductsTable, {
  confirmDraftKey,
} from "@/features/line-sheet-sets/all-ordered-products/all-ordered-products-table";
import AppButton from "@/features/line-sheet-sets/app-button";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { useConfirmBoutiqueOrderSheetQuantities } from "@/features/line-sheet-sets/hooks/use-confirm-boutique-ordered-quantities";
import {
  AllOrderedProduct,
  AllOrderedProductOption,
} from "@/features/line-sheet-sets/hooks/use-get-boutique-all-ordered-products";
import {
  displayLineSheetName,
  filterProducts,
  PriceAdjustmentDraft,
  summarize,
} from "@/features/line-sheet-sets/all-ordered-products/all-ordered-products-helpers";

/**
 * (부티크) 라인시트세트에 들어온 모든 바이어의 오더를 상품·사이즈별로 합쳐 보는 화면.
 *
 * 부티크가 상품별로 총 몇 개를 준비해야 하는지 오더시트를 하나씩 열지 않고 본다.
 *
 * 라인시트세트 상세의 주문 목록에서 오더 한 줄을 누르면 이 화면이 그 오더 한 건만 보여주고,
 * 옵션마다 확정 수량을, 상품마다 확정 가격조정을 넣어 저장할 수 있다(오더시트 화면의 저장과 같게 새 리비전으로
 * 남고, 확정 단가는 서버가 오더시트 화면과 같은 식으로 다시 계산한다).
 * 세트 전체를 볼 때는 조회 전용이다.
 *
 * 세트 전체를 한 번 받아 두고 라인시트·검색어는 화면에서 거른다.
 */
export default function BoutiqueAllOrderedProductsPage() {
  const identity = useIdentity();
  const { t, tTitle } = useI18nHelper();
  const { lineSheetSetId: rawLineSheetSetId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState("");

  const lineSheetSetId = Number(rawLineSheetSetId);
  const rawLineSheetId = Number(searchParams.get("line_sheet_id"));
  const lineSheetId =
    Number.isInteger(rawLineSheetId) && rawLineSheetId > 0
      ? rawLineSheetId
      : null;
  // 라인시트세트 상세의 주문 목록에서 오더 줄을 누르고 오면 그 오더(오더시트세트) 한 건만 본다
  const rawOrderSheetSetId = Number(searchParams.get("order_sheet_set_id"));
  const orderSheetSetId =
    Number.isInteger(rawOrderSheetSetId) && rawOrderSheetSetId > 0
      ? rawOrderSheetSetId
      : undefined;

  const isBoutique = identity?.company?.type === "BOUTIQUE";
  const { data, isLoading, error, mutate } = useGetBoutiqueAllOrderedProducts(
    isBoutique && identity?.company && Number.isInteger(lineSheetSetId)
      ? { boutiqueId: identity.company.id, lineSheetSetId }
      : null,
    { orderSheetSetId },
    { revalidateOnFocus: false }
  );

  const lineSheetNameById = useMemo(
    () => new Map((data?.lineSheets ?? []).map((ls) => [ls.id, ls.name])),
    [data]
  );

  const products = useMemo(
    () => filterProducts(data?.products ?? [], { lineSheetId, query }),
    [data, lineSheetId, query]
  );
  const summary = useMemo(() => summarize(products), [products]);

  // ── 오더 한 건을 볼 때 확정 수량 입력 ──
  const order = data?.order ?? null;
  const [drafts, setDrafts] = useState<Record<string, number>>({});
  // 상품(라인시트 상품 id)별로 고친 확정 가격조정
  const [priceDrafts, setPriceDrafts] = useState<
    Record<number, PriceAdjustmentDraft>
  >({});
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    setDrafts({});
    setPriceDrafts({});
  }, [orderSheetSetId]);
  const { error: showError, info: showInfo } = useAppToasts();
  const { fire: fireOrderSheetConfirm } =
    useConfirmBoutiqueOrderSheetQuantities();

  const confirmInput = useMemo(
    () =>
      order?.confirmable
        ? {
            draftOf: (
              product: AllOrderedProduct,
              option: AllOrderedProductOption
            ) => drafts[confirmDraftKey(product, option)],
            onChange: (
              product: AllOrderedProduct,
              option: AllOrderedProductOption,
              value: number
            ) =>
              setDrafts((prev) => ({
                ...prev,
                [confirmDraftKey(product, option)]: value,
              })),
            priceDraftOf: (product: AllOrderedProduct) =>
              priceDrafts[product.id],
            onPriceChange: (
              product: AllOrderedProduct,
              draft: PriceAdjustmentDraft
            ) => setPriceDrafts((prev) => ({ ...prev, [product.id]: draft })),
            order,
            isDisabled: isSaving,
          }
        : undefined,
    [order, drafts, priceDrafts, isSaving]
  );

  // 보이는 옵션을 전부 저장한다 — 입력칸에 보이는 값(입력값 / 저장된 확정 / 주문 수량) 그대로.
  const saveConfirmed = useCallback(async () => {
    if (!order?.confirmable || !identity?.company) {
      return;
    }
    const lines = products.flatMap((product) =>
      product.orderQuantityWithOptionList.map((option) => ({
        option,
        value:
          drafts[confirmDraftKey(product, option)] ??
          option.confirmedQuantity ??
          option.quantity,
      }))
    );
    // 확정 가격조정은 저장된 값과 달라진 상품만 보낸다
    const prices = products.flatMap((product) => {
      const draft = priceDrafts[product.id];
      const saved = product.orderPrice?.priceAdjustedBy;
      const orderSheetId = product.orderQuantityWithOptionList.find(
        (option) => option.orderSheetId !== null
      )?.orderSheetId;
      if (
        !draft ||
        !orderSheetId ||
        (saved && saved.by === draft.by && Number(saved.value) === draft.value)
      ) {
        return [];
      }
      return [
        {
          orderSheetId,
          productId: product.id,
          by: draft.by,
          value: draft.value,
        },
      ];
    });
    const path = { boutiqueId: identity.company.id, lineSheetSetId };
    setIsSaving(true);
    try {
      await fireOrderSheetConfirm(
        { ...path, orderSheetSetId: order.id },
        {
          options: lines.flatMap(({ option, value }) =>
            option.orderSheetId === null || option.productOptionId === null
              ? []
              : [
                  {
                    orderSheetId: option.orderSheetId,
                    productOptionId: option.productOptionId,
                    confirmedQuantity: value,
                  },
                ]
          ),
          prices,
        },
        { rethrow: true }
      );
      setDrafts({});
      setPriceDrafts({});
      await mutate();
      showInfo(t("all_ordered_products.confirm_saved"));
    } catch (e) {
      showError(e);
    } finally {
      setIsSaving(false);
    }
  }, [
    order,
    identity,
    products,
    drafts,
    priceDrafts,
    lineSheetSetId,
    fireOrderSheetConfirm,
    mutate,
    showInfo,
    showError,
    t,
  ]);

  const quantityText = (quantity: number) =>
    `${quantity.toLocaleString()}${t("quantity")}`;

  return (
    <Grid
      height={"100%"}
      width={"100%"}
      gridTemplateColumns={"minmax(0, 1fr)"}
      gridTemplateRows={"auto auto 1fr"}
    >
      <AppListHeader
        showNavigateToBack={true}
        emptyStackPage={`/line-sheet-sets/${lineSheetSetId}`}
        showFilterText={false}
        headers={[
          <Text
            key={"AllOrderedProducts_Title"}
            fontSize={"14px"}
            fontWeight={"700"}
            flexShrink={0}
          >
            {tTitle("all_ordered_products.title")}
          </Text>,
          ...(order?.confirmable
            ? [
                <AppButton
                  key={"AllOrderedProducts_SaveConfirmed"}
                  variant={"primary"}
                  isLoading={isSaving}
                  isDisabled={isSaving || products.length === 0}
                  onClick={saveConfirmed}
                >
                  {tTitle("all_ordered_products.save_confirmed")}
                </AppButton>,
              ]
            : []),
        ]}
        filters={[]}
        figures={[
          {
            name: tTitle("ordered_quantity"),
            value: quantityText(summary.quantity),
          },
          {
            name: tTitle("confirmed_quantity"),
            value: quantityText(summary.confirmedQuantity),
          },
        ]}
      />
      <AppListHeader
        showFilterText={false}
        filters={[
          <Select
            key={"AllOrderedProducts_LineSheet"}
            aria-label={tTitle("line_sheet")}
            size={"sm"}
            width={"280px"}
            flexShrink={0}
            fontSize={"12px"}
            borderRadius={"4px"}
            value={lineSheetId ?? ""}
            onChange={(event) => {
              const next = new URLSearchParams(searchParams);
              if (event.target.value) {
                next.set("line_sheet_id", event.target.value);
              } else {
                next.delete("line_sheet_id");
              }
              setSearchParams(next, { replace: true });
            }}
          >
            <option value={""}>
              {`${tTitle("all")} (${quantityText(
                data?.summary.quantity ?? 0
              )})`}
            </option>
            {(data?.lineSheets ?? []).map((lineSheet) => (
              <option key={lineSheet.id} value={lineSheet.id}>
                {`${displayLineSheetName(lineSheet.name)} (${quantityText(
                  lineSheet.quantity
                )})`}
              </option>
            ))}
          </Select>,
          <AppQueryController
            key={"AllOrderedProducts_Query"}
            value={query}
            showButton={false}
            placeholder={tTitle("all_ordered_products.search_placeholder")}
            leadingIcon="search"
            onChange={(value) => setQuery(value ?? "")}
          />,
        ]}
      />
      <Box overflow={"auto"} background={"#FFFFFF"}>
        {!isBoutique ? (
          <CenterMessage>
            {t("all_ordered_products.boutique_only")}
          </CenterMessage>
        ) : isLoading ? (
          <Flex justifyContent={"center"} padding={"48px"}>
            <Spinner size={"md"} />
          </Flex>
        ) : error ? (
          <CenterMessage>{t("all_ordered_products.load_failed")}</CenterMessage>
        ) : products.length === 0 ? (
          <CenterMessage>
            {data && data.products.length > 0
              ? t("no_results")
              : t("all_ordered_products.empty")}
          </CenterMessage>
        ) : (
          <AllOrderedProductsTable
            products={products}
            lineSheetNameById={lineSheetNameById}
            showLineSheet={lineSheetId === null}
            confirmInput={confirmInput}
          />
        )}
      </Box>
    </Grid>
  );
}

function CenterMessage({ children }: { children: ReactNode }) {
  return (
    <Flex justifyContent={"center"} padding={"48px"}>
      <Text fontSize={"13px"} color={"#6F6F6C"}>
        {children}
      </Text>
    </Flex>
  );
}
